import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";
import SearchIcon from "@mui/icons-material/Search";

import { useUserContext } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";
import {
  AvatarGroup,
  AvatarGroupItem,
  CustomPhotoAvatar,
  HeygenVoice,
  ListAvatarGroupsV3Response,
} from "../../context/types";
import {
  checkPhotoAvatarStatus,
  createAvatarFromUrl,
  createCustomPhotoAvatarV3FromImage,
  createCustomPhotoAvatarV3FromPrompt,
  createCustomPhotoAvatarV3FromVideo,
  deleteAvatarGroup,
  deleteCustomPhotoAvatar,
  getAvatarGroupsByOrganization,
  getAvatarGroupsByWorkspace,
  getCustomPhotoAvatarV3Group,
  getCustomPhotoAvatarsByOrganization,
  getCustomPhotoAvatarsByWorkspace,
  listAllHeygenVoices,
  listCustomPhotoAvatarV3Groups,
  syncCustomPhotoAvatarV3Look,
  updateAvatarGroupDefaultVoice,
  updateAvatarLook,
} from "../../client/api";
import AvatarCreationModal from "../../components/ActorsDialog/components/AvatarCreationModal";
import ChooseLookModal from "../../components/ActorsDialog/components/ChooseLookModal";
import LookCreationModal from "../../components/ActorsDialog/components/LookCreationModal";
import UnifiedAvatarCard from "../../components/ActorsDialog/components/UnifiedAvatarCard";
import useActorsStyles from "../../components/ActorsDialog/styles";
import { THEME_COLORS } from "../../context/constants";
import { buildAvatarPrompt } from "../../utils/buildAvatarPrompt";
import EditAvatarDialog from "./EditAvatarDialog";
import EditGroupVoiceDialog from "./EditGroupVoiceDialog";
import useStyles from "./styles";

const AVATAR_LAB_FILTERS = [
  "All",
  "Male",
  "Female",
  "Processing",
  "Completed",
  "Failed",
];

const AvatarDesign: React.FC = () => {
  const classes = useStyles();
  const actorsClasses = useActorsStyles();
  const { user, currentWorkspace } = useUserContext();
  const { addNotification } = useNotification();

  const [avatars, setAvatars] = useState<CustomPhotoAvatar[]>([]);
  const [groups, setGroups] = useState<AvatarGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("All");
  const [sourceFilter, setSourceFilter] = useState<"organization" | "workspace">(
    "organization"
  );

  // Creation
  const [creationOpen, setCreationOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Edit dialog (single avatar)
  const [editingAvatar, setEditingAvatar] = useState<CustomPhotoAvatar | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Edit dialog (group-level default voice)
  const [editingGroup, setEditingGroup] = useState<AvatarGroup | null>(null);
  const [editGroupDialogOpen, setEditGroupDialogOpen] = useState(false);

  // Look creation / look picker dialogs (per-group three-dot menu)
  const [selectedGroupForLook, setSelectedGroupForLook] =
    useState<AvatarGroup | null>(null);
  const [lookCreationModalOpen, setLookCreationModalOpen] = useState(false);
  const [chooseLookModalOpen, setChooseLookModalOpen] = useState(false);
  const [isCreatingLook, setIsCreatingLook] = useState(false);

  // Voices for the edit dialog
  const [voices, setVoices] = useState<HeygenVoice[]>([]);
  const [voicesLoading, setVoicesLoading] = useState(false);

  // Status pollers for avatars in 'processing'. Tracked in a ref (not state) so
  // sync updates that call setAvatars don't cancel the in-flight poller via
  // effect cleanup. Mirrors the pattern in ActorsDialog.
  const pollersRef = useRef<Map<string, { cancel: () => void }>>(new Map());

  const fetchAvatars = async () => {
    if (!currentWorkspace?._id) return;
    try {
      setIsLoading(true);
      const list =
        sourceFilter === "organization" && user?.organization?._id
          ? await getCustomPhotoAvatarsByOrganization(user.organization._id)
          : await getCustomPhotoAvatarsByWorkspace(currentWorkspace._id);
      const sorted = [...list].sort(
        (a, b) =>
          new Date((b as any).createdAt || 0).getTime() -
          new Date((a as any).createdAt || 0).getTime()
      );
      setAvatars(sorted);
    } catch (err: any) {
      console.error("Error fetching custom photo avatars:", err);
      addNotification(
        err?.response?.data?.message || "Failed to load avatars",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGroups = async () => {
    if (!currentWorkspace?._id) return;
    try {
      const list =
        sourceFilter === "organization" && user?.organization?._id
          ? await getAvatarGroupsByOrganization(user.organization._id)
          : await getAvatarGroupsByWorkspace(currentWorkspace._id);
      setGroups(list);
    } catch (err) {
      console.error("Error fetching avatar groups:", err);
    }
  };

  useEffect(() => {
    fetchAvatars();
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?._id, sourceFilter]);

  // Lazy-load voices the first time either edit dialog opens; cache thereafter.
  useEffect(() => {
    if (!editDialogOpen && !editGroupDialogOpen) return;
    if (voices.length > 0 || voicesLoading) return;
    let cancelled = false;
    (async () => {
      try {
        setVoicesLoading(true);
        const res = await listAllHeygenVoices();
        if (!cancelled) setVoices(res.data || []);
      } catch (err) {
        console.error("Error loading HeyGen voices:", err);
        if (!cancelled) addNotification("Failed to load voices", "error");
      } finally {
        if (!cancelled) setVoicesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDialogOpen, editGroupDialogOpen]);

  // ===== Status polling for newly-created / processing avatars =====
  // Mirrors ActorsDialog. V3 uses per-look sync with exponential backoff
  // (5s for first 30s, then 15s, hard cap 10 min). V2 falls back to a 20s
  // setInterval against the legacy generation_id-based status endpoint.
  const TERMINAL_V3_STATES = new Set([
    "success",
    "completed",
    "failed",
    "pending_consent",
  ]);

  const checkAvatarStatusV2 = async (generationId: string) => {
    try {
      const response = await checkPhotoAvatarStatus(generationId);
      if (response.avatar) {
        setAvatars((prev) =>
          prev.map((a) =>
            a.generation_id === generationId ? response.avatar : a
          )
        );
      }
    } catch (err) {
      console.error("Error checking avatar status (V2):", err);
    }
  };

  const checkAvatarStatusV3 = async (
    avatar: CustomPhotoAvatar
  ): Promise<boolean> => {
    if (!avatar.avatar_look_id) {
      // Older V3 records without a look_id — fall back to group polling.
      if (!avatar.group_id) return true;
      try {
        const response = await getCustomPhotoAvatarV3Group(avatar.group_id);
        return TERMINAL_V3_STATES.has(response.data.status);
      } catch (err) {
        console.error("Error checking V3 avatar group status:", err);
        return false;
      }
    }

    try {
      const { avatar: updatedAvatar } = await syncCustomPhotoAvatarV3Look(
        avatar.avatar_look_id
      );
      setAvatars((prev) =>
        prev.map((a) =>
          a._id === avatar._id ? { ...a, ...updatedAvatar } : a
        )
      );
      const isTerminal = TERMINAL_V3_STATES.has(updatedAvatar.status);
      if (isTerminal) {
        // Refresh groups so a newly-completed look shows up inside its
        // populated group.looks array.
        await fetchGroups();
      }
      return isTerminal;
    } catch (err) {
      console.error("Error syncing V3 avatar look status:", err);
      return false;
    }
  };

  useEffect(() => {
    if (!avatars) return;
    const processing = avatars.filter((a) => a.status === "processing");
    const activeKeys = new Set<string>();

    processing.forEach((avatar) => {
      const pollKey =
        avatar.apiVersion === "v3"
          ? avatar.avatar_look_id || avatar.group_id
          : avatar.generation_id;
      if (!pollKey) return;
      activeKeys.add(pollKey);
      if (pollersRef.current.has(pollKey)) return;

      if (avatar.apiVersion === "v3") {
        const startTime = Date.now();
        const MAX_DURATION = 10 * 60 * 1000;
        let cancelled = false;
        let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

        const tick = async () => {
          if (cancelled) return;
          const reachedTerminal = await checkAvatarStatusV3(avatar);
          if (cancelled) return;
          if (reachedTerminal || Date.now() - startTime >= MAX_DURATION) {
            pollersRef.current.delete(pollKey);
            return;
          }
          const elapsed = Date.now() - startTime;
          const delay = elapsed > 30_000 ? 15_000 : 5_000;
          timeoutHandle = setTimeout(tick, delay);
        };

        timeoutHandle = setTimeout(tick, 5_000);
        pollersRef.current.set(pollKey, {
          cancel: () => {
            cancelled = true;
            if (timeoutHandle) clearTimeout(timeoutHandle);
          },
        });
      } else if (avatar.generation_id) {
        const interval = setInterval(() => {
          checkAvatarStatusV2(avatar.generation_id!);
        }, 20000);
        pollersRef.current.set(pollKey, {
          cancel: () => clearInterval(interval),
        });
      }
    });

    // Cancel pollers for avatars that have left the processing set.
    for (const [key, poller] of Array.from(pollersRef.current.entries())) {
      if (!activeKeys.has(key)) {
        poller.cancel();
        pollersRef.current.delete(key);
      }
    }
    // No cleanup return — cleanup must not fire on every re-run, else sync-
    // triggered setAvatars updates would cancel the very poller that just
    // triggered them. Unmount cleanup lives in the dedicated effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatars]);

  // Cancel all pollers on unmount.
  useEffect(() => {
    const pollers = pollersRef.current;
    return () => {
      Array.from(pollers.values()).forEach((p) => p.cancel());
      pollers.clear();
    };
  }, []);

  // ===== Group helpers (mirror ActorsDialog) =====
  const groupHasValidLooks = (group: AvatarGroup): boolean => {
    if (!group.looks || group.looks.length === 0) return false;
    return group.looks.some(
      (look) =>
        typeof look === "object" &&
        look !== null &&
        look.image_url_list &&
        look.image_url_list.length > 0
    );
  };

  const standaloneAvatars = useMemo(() => {
    return avatars.filter((avatar) => {
      const mongoGroupId = avatar.avatar_group as string | undefined;
      const heygenGroupId = avatar.group_id;
      if (!mongoGroupId && !heygenGroupId) return true;

      const group = groups.find(
        (g) =>
          (mongoGroupId && g._id === mongoGroupId) ||
          (heygenGroupId && g.group_id === heygenGroupId)
      );
      if (!group || group.training_status !== "ready") return true;
      if (!groupHasValidLooks(group)) return true;
      return false;
    });
  }, [avatars, groups]);

  const filteredAvatars = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return standaloneAvatars.filter((avatar) => {
      switch (activeFilter) {
        case "Male":
          if (avatar.gender !== "male") return false;
          break;
        case "Female":
          if (avatar.gender !== "female") return false;
          break;
        case "Processing":
          if (avatar.status !== "processing") return false;
          break;
        case "Completed":
          if (avatar.status !== "success") return false;
          break;
        case "Failed":
          if (avatar.status !== "failed") return false;
          break;
        default:
          break;
      }
      if (!q) return true;
      return avatar.name.toLowerCase().includes(q);
    });
  }, [standaloneAvatars, activeFilter, searchQuery]);

  const readyGroups = useMemo(
    () =>
      groups.filter(
        (g) => g.training_status === "ready" && groupHasValidLooks(g)
      ),
    [groups]
  );

  const isAvatarGroupCreating = (avatar: CustomPhotoAvatar): boolean => {
    if (avatar.status === "success" && avatar.image_url_list?.length > 0) {
      return false;
    }
    const mongoGroupId = avatar.avatar_group as string | undefined;
    const heygenGroupId = avatar.group_id;
    if (!mongoGroupId && !heygenGroupId) return false;
    const group = groups.find(
      (g) =>
        (mongoGroupId && g._id === mongoGroupId) ||
        (heygenGroupId && g.group_id === heygenGroupId)
    );
    if (!group) return true;
    if (group.training_status !== "ready") return true;
    if (!groupHasValidLooks(group)) return true;
    return false;
  };

  // ===== Handlers =====
  const handleDelete = async (avatarId: string) => {
    try {
      await deleteCustomPhotoAvatar(avatarId);
      addNotification("Avatar deleted successfully", "success");
      await fetchAvatars();
      await fetchGroups();
    } catch (err) {
      console.error("Error deleting avatar:", err);
      addNotification("Error deleting avatar", "error");
    }
  };

  // Groups need the dedicated /api/avatar-groups DELETE endpoint — calling
  // deleteCustomPhotoAvatar with a group's _id 500s because no
  // CustomPhotoAvatar exists at that id.
  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteAvatarGroup(groupId);
      addNotification("Avatar group deleted successfully", "success");
      await fetchAvatars();
      await fetchGroups();
    } catch (err) {
      console.error("Error deleting avatar group:", err);
      addNotification("Error deleting avatar group", "error");
    }
  };

  const handleOpenGroupEdit = (group: AvatarGroup) => {
    setEditingGroup(group);
    setEditGroupDialogOpen(true);
  };

  const handleSaveGroupVoice = async (payload: {
    default_voice_id: string | null;
  }) => {
    if (!editingGroup) return;
    try {
      await updateAvatarGroupDefaultVoice(editingGroup._id, payload.default_voice_id);
      addNotification("Group default voice updated", "success");
      setEditGroupDialogOpen(false);
      setEditingGroup(null);
      await fetchGroups();
      await fetchAvatars();
    } catch (err: any) {
      console.error("Error updating group default voice:", err);
      addNotification(
        err?.response?.data?.message || "Failed to update group default voice",
        "error"
      );
    }
  };

  const handleRename = async (avatar: CustomPhotoAvatar, newName: string) => {
    try {
      const res = await updateAvatarLook(avatar._id, { name: newName });
      if (res.avatar) {
        setAvatars((prev) =>
          prev.map((a) => (a._id === avatar._id ? res.avatar : a))
        );
      } else {
        await fetchAvatars();
      }
      addNotification("Avatar renamed", "success");
    } catch (err: any) {
      console.error("Error renaming avatar:", err);
      addNotification(
        err?.response?.data?.message || "Failed to rename avatar",
        "error"
      );
    }
  };

  const handleOpenEdit = (avatar: CustomPhotoAvatar) => {
    setEditingAvatar(avatar);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async (payload: {
    name: string;
    default_voice_id: string | null;
  }) => {
    if (!editingAvatar) return;
    try {
      const apiPayload: { name?: string; default_voice_id?: string } = {};
      if (payload.name && payload.name !== editingAvatar.name) {
        apiPayload.name = payload.name;
      }
      if (payload.default_voice_id !== (editingAvatar.default_voice_id ?? null)) {
        // Backend payload uses string; empty string clears it.
        apiPayload.default_voice_id = payload.default_voice_id ?? "";
      }
      if (Object.keys(apiPayload).length === 0) {
        setEditDialogOpen(false);
        setEditingAvatar(null);
        return;
      }
      const res = await updateAvatarLook(editingAvatar._id, apiPayload);
      if (res.avatar) {
        setAvatars((prev) =>
          prev.map((a) => (a._id === editingAvatar._id ? res.avatar : a))
        );
      } else {
        await fetchAvatars();
      }
      addNotification("Avatar updated", "success");
      setEditDialogOpen(false);
      setEditingAvatar(null);
    } catch (err: any) {
      console.error("Error updating avatar:", err);
      addNotification(
        err?.response?.data?.message || "Failed to update avatar",
        "error"
      );
    }
  };

  // V3 avatar group picker for the creation modal (same flow as ActorsDialog).
  const fetchV3AvatarGroups = async (
    organizationId: string,
    maxItems: number = 100
  ): Promise<AvatarGroupItem[]> => {
    const all: AvatarGroupItem[] = [];
    let token: string | undefined = undefined;
    try {
      do {
        const res: ListAvatarGroupsV3Response = await listCustomPhotoAvatarV3Groups({
          organizationId,
          limit: 50,
          token,
        });
        all.push(...res.data);
        token = res.has_more ? res.next_token : undefined;
      } while (token && all.length < maxItems);
      return all.slice(0, maxItems);
    } catch (err) {
      console.error("Error listing V3 avatar groups:", err);
      addNotification("Error loading existing identities", "error");
      return [];
    }
  };

  const handleCreateAvatar = async (
    formData: any,
    type: "generate" | "upload" | "library" | "video",
    imageFile?: File,
    assetUrl?: string,
    videoFile?: File
  ) => {
    if (!currentWorkspace || !user) return;
    try {
      setIsCreating(true);

      if (type === "generate") {
        await createCustomPhotoAvatarV3FromPrompt({
          workspaceId: currentWorkspace._id,
          userEmail: user.email,
          name: formData.name,
          prompt: buildAvatarPrompt(formData) || formData.prompt,
          ...(formData.avatarGroupId && {
            avatarGroupId: formData.avatarGroupId,
            referenceImages: formData.referenceImages,
          }),
        } as any);
      } else if (type === "upload") {
        await createCustomPhotoAvatarV3FromImage({
          workspaceId: currentWorkspace._id,
          userEmail: user.email,
          name: formData.name,
          avatarGroupId: formData.avatarGroupId,
          image: imageFile!,
        } as any);
      } else if (type === "video") {
        await createCustomPhotoAvatarV3FromVideo({
          workspaceId: currentWorkspace._id,
          userEmail: user.email,
          name: formData.name,
          avatarGroupId: formData.avatarGroupId,
          video: videoFile!,
        } as any);
      } else if (type === "library" && assetUrl) {
        await createAvatarFromUrl({
          workspaceId: currentWorkspace._id,
          userEmail: user.email,
          ...formData,
          imageUrl: assetUrl,
        } as any);
      }

      await fetchAvatars();
      await fetchGroups();
      setCreationOpen(false);
      addNotification(
        `Avatar ${type === "generate" ? "generated" : "created"} successfully`,
        "success"
      );
    } catch (err: any) {
      console.error("Error creating avatar:", err);
      addNotification(
        err?.response?.data?.message ||
          `Error ${type === "generate" ? "generating" : "creating"} avatar, please try again.`,
        "error"
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleRefreshGroup = async () => {
    await fetchGroups();
  };

  // Group three-dot menu — "Add New Look" / "Choose looks". Mirrors ActorsDialog.
  const handleOpenGroupLookCreation = (group: AvatarGroup) => {
    setSelectedGroupForLook(group);
    setLookCreationModalOpen(true);
  };

  const handleOpenChooseLookModal = (group: AvatarGroup) => {
    setSelectedGroupForLook(group);
    setChooseLookModalOpen(true);
  };

  const handleCreateLook = async (
    formData: any,
    type: "generate" | "upload",
    imageFile?: File
  ) => {
    if (!selectedGroupForLook || !currentWorkspace || !user) return;
    try {
      setIsCreatingLook(true);
      if (type === "generate") {
        await createCustomPhotoAvatarV3FromPrompt({
          workspaceId: currentWorkspace._id,
          userEmail: user.email,
          name: formData.name,
          prompt: buildAvatarPrompt(formData) || formData.prompt,
          avatarGroupId: selectedGroupForLook.group_id,
          ...(formData.referenceImages && {
            referenceImages: formData.referenceImages,
          }),
        });
      } else {
        await createCustomPhotoAvatarV3FromImage({
          workspaceId: currentWorkspace._id,
          userEmail: user.email,
          name: formData.name,
          avatarGroupId: selectedGroupForLook.group_id,
          image: imageFile!,
        });
      }
      addNotification(
        `Look "${formData.name}" creation started successfully`,
        "success"
      );
      setLookCreationModalOpen(false);
      await fetchGroups();
      await fetchAvatars();
    } catch (err: any) {
      console.error("Error creating look:", err);
      addNotification(
        err?.response?.data?.message || "Error creating look, please try again.",
        "error"
      );
    } finally {
      setIsCreatingLook(false);
    }
  };

  const handleDeleteLook = async (lookId: string) => {
    try {
      await deleteCustomPhotoAvatar(lookId);
      addNotification("Look deleted", "success");
      await fetchAvatars();
      await fetchGroups();
    } catch (err) {
      console.error("Error deleting look:", err);
      addNotification("Error deleting look", "error");
    }
  };

  const showEmpty = !isLoading && readyGroups.length === 0 && standaloneAvatars.length === 0;
  const showNoMatches =
    !isLoading &&
    !showEmpty &&
    filteredAvatars.length === 0 &&
    readyGroups.length === 0 &&
    (searchQuery.trim().length > 0 || activeFilter !== "All");

  return (
    <Box className={classes.container}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.titleRow}>
          <FaceRetouchingNaturalIcon className={classes.titleIcon} />
          <Typography className={classes.title}>Avatar Design</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreationOpen(true)}
          className={classes.createButton}
          disabled={!currentWorkspace?._id}
        >
          Create New Avatar
        </Button>
      </Box>
      <Typography className={classes.subtitle}>
        Manage your custom avatars and assign default voices.
      </Typography>

      {/* Filters + search */}
      <Box className={classes.controlsRow}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={sourceFilter}
            onChange={(e) =>
              setSourceFilter(e.target.value as "organization" | "workspace")
            }
            sx={{
              borderRadius: "10px",
              fontFamily: "Lexend, sans-serif",
              backgroundColor: "#fafafa",
            }}
          >
            <MenuItem value="organization">Organization</MenuItem>
            <MenuItem value="workspace">This Workspace</MenuItem>
          </Select>
        </FormControl>

        <Box className={classes.chipsRow}>
          {AVATAR_LAB_FILTERS.map((filter) => (
            <Chip
              key={filter}
              label={filter}
              clickable
              color={activeFilter === filter ? "primary" : "default"}
              onClick={() => setActiveFilter(filter)}
            />
          ))}
        </Box>

        <Box className={classes.searchBar}>
          <SearchIcon sx={{ color: "#bbb", fontSize: 20 }} />
          <InputBase
            placeholder="Search avatars..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            sx={{ fontFamily: "Lexend, sans-serif", fontSize: 14 }}
          />
        </Box>
      </Box>

      <Typography className={classes.sourceLine}>
        Showing avatars from:{" "}
        <strong>
          {sourceFilter === "organization"
            ? `Entire Organization${
                user?.organization?.name ? ` (${user.organization.name})` : ""
              }`
            : `This Workspace (${currentWorkspace?.name || "Current"})`}
        </strong>
      </Typography>

      {/* Body */}
      {isLoading ? (
        <Box className={classes.loadingWrapper}>
          <CircularProgress sx={{ color: THEME_COLORS.yellow }} />
        </Box>
      ) : showEmpty ? (
        <Box className={classes.emptyState}>
          <Box className={classes.emptyIconWrapper}>
            <FaceRetouchingNaturalIcon className={classes.emptyIcon} />
          </Box>
          <Typography className={classes.emptyTitle}>
            You haven't created any avatars yet
          </Typography>
          <Typography className={classes.emptySubtitle}>
            Build a custom avatar from a prompt, image, or video. You'll be able
            to assign each avatar a default voice for video generation.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreationOpen(true)}
            className={classes.createButton}
          >
            Create your first avatar
          </Button>
        </Box>
      ) : showNoMatches ? (
        <Box className={classes.emptyState}>
          <Typography className={classes.emptyTitle}>
            No avatars match the current filters.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2} className={actorsClasses.actorsGrid}>
          {readyGroups.map((group) => (
            <Grid item xs={6} sm={4} md={3} key={group._id}>
              <Box sx={{ position: "relative" }}>
                <UnifiedAvatarCard
                  item={group}
                  type="group"
                  canSelect={false}
                  onOpenGroupLookCreation={handleOpenGroupLookCreation}
                  onOpenChooseLookModal={handleOpenChooseLookModal}
                  onRefreshGroup={handleRefreshGroup}
                  onDeleteGroup={handleDeleteGroup}
                  onSetGroupDefaultVoice={handleOpenGroupEdit}
                  classes={actorsClasses}
                />
              </Box>
            </Grid>
          ))}

          {filteredAvatars.map((avatar) => {
            const groupCreating = isAvatarGroupCreating(avatar);
            const canRename =
              avatar.apiVersion === "v3" &&
              !!avatar.avatar_look_id &&
              avatar.avatarType !== "prompt";
            return (
              <Grid item xs={6} sm={4} md={3} key={avatar._id}>
                <Box sx={{ position: "relative" }}>
                  <UnifiedAvatarCard
                    item={avatar}
                    type="avatar"
                    canSelect={false}
                    isGroupCreating={groupCreating}
                    onDeleteAvatar={handleDelete}
                    onOpenGroupLookCreation={() => undefined}
                    onOpenChooseLookModal={() => undefined}
                    onRefreshGroup={handleRefreshGroup}
                    classes={actorsClasses}
                    onRenameLook={
                      canRename
                        ? (newName: string) => handleRename(avatar, newName)
                        : undefined
                    }
                  />
                  {avatar.status === "success" && (
                    <Tooltip title="Edit avatar (name + default voice)">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEdit(avatar);
                        }}
                        className={classes.editFab}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Avatar Creation Modal — same wiring as ActorsDialog */}
      <AvatarCreationModal
        open={creationOpen}
        onClose={() => setCreationOpen(false)}
        onSubmit={handleCreateAvatar}
        isLoading={isCreating}
        loadExistingIdentities={
          user?.organization?._id
            ? () => fetchV3AvatarGroups(user.organization._id)
            : undefined
        }
      />

      {/* Edit Avatar Dialog */}
      <EditAvatarDialog
        open={editDialogOpen}
        avatar={editingAvatar}
        voices={voices}
        voicesLoading={voicesLoading}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingAvatar(null);
        }}
        onSave={handleSaveEdit}
      />

      {/* Edit Group Default Voice Dialog */}
      <EditGroupVoiceDialog
        open={editGroupDialogOpen}
        group={editingGroup}
        voices={voices}
        voicesLoading={voicesLoading}
        onClose={() => {
          setEditGroupDialogOpen(false);
          setEditingGroup(null);
        }}
        onSave={handleSaveGroupVoice}
      />

      {/* Add New Look — for an existing avatar group */}
      {selectedGroupForLook && (
        <LookCreationModal
          open={lookCreationModalOpen}
          onClose={() => {
            setLookCreationModalOpen(false);
            setSelectedGroupForLook(null);
          }}
          onSubmit={handleCreateLook}
          isLoading={isCreatingLook}
          group={selectedGroupForLook}
        />
      )}

      {/* Manage Looks — pick/manage looks inside a group */}
      {selectedGroupForLook && (
        <ChooseLookModal
          open={chooseLookModalOpen}
          onClose={() => {
            setChooseLookModalOpen(false);
            setSelectedGroupForLook(null);
          }}
          group={selectedGroupForLook}
          selectedAvatarLabActors={[]}
          onSelectAvatarLabActor={() => undefined}
          onDeleteLook={handleDeleteLook}
        />
      )}
    </Box>
  );
};

export default AvatarDesign;
